import DeleteIcon from "@mui/icons-material/Delete";
import {
  RaRecord,
  useCreatePath,
  useGetRecordRepresentation,
  useRecordContext,
  useRefresh,
  useResourceContext,
} from "react-admin";
import { SimpleConfirmDialogButton } from "../misc/SimpleConfirmDialogButton";
import { useNavigate } from "react-router";
import { ReactNode } from "react";

/**
 * Replacement for the react-admin `DeleteButton` and `DeleteWithConfirmButton`.
 */
type DeleteRecordButtonProps<RecordType extends RaRecord = any> = {
  disabled?: boolean;
  icon?: ReactNode;
  label?: string;
  confirmDialogTitle?: string;
  confirmDialogContent?: ReactNode;
  successMessage?: string;
  meta?: Record<string, unknown>;
  onError?: (error: unknown) => void;
} & (
  | {
      onSuccessRedirectTo?: string;
      onSuccess?: never;
    }
  | {
      onSuccessRedirectTo?: never;
      onSuccess: (record: RecordType) => void;
    }
);

export function DeleteRecordButton(props: DeleteRecordButtonProps) {
  const {
    disabled,
    icon = <DeleteIcon />,
    label = "Löschen",
    confirmDialogTitle,
    confirmDialogContent,
    successMessage,
    meta,
    onSuccessRedirectTo,
    onSuccess,
    onError,
  } = props;
  const resource = useResourceContext();
  const getRecordRepresentation = useGetRecordRepresentation(resource);
  const record = useRecordContext();
  const refresh = useRefresh();
  const navigate = useNavigate();
  const createPath = useCreatePath();
  const recordRepresentation = record ? (getRecordRepresentation(record) as string) : "";

  return (
    <SimpleConfirmDialogButton
      icon={icon}
      color="error"
      label={label}
      disabled={disabled}
      dialogTitle={confirmDialogTitle ?? `${recordRepresentation} wirklich löschen?`}
      dialogContent={confirmDialogContent}
      action={() => dataProvider.delete(resource, { id: record.id, meta })}
      actionDisabled={!record}
      onSuccess={() => {
        if (onSuccess) {
          onSuccess(record);
        } else {
          refresh();
          showSuccessToast(successMessage ?? `${recordRepresentation} erfolgreich gelöscht.`);
          navigate(onSuccessRedirectTo ?? createPath({ resource, type: "list" }));
        }
      }}
      onError={onError}
    />
  );
}
